import { useEffect, useRef, useState } from "react";
import { Type, Trash2, Copy, RotateCw, FlipHorizontal, Bold, AlignLeft, AlignCenter, AlignRight, BringToFront, SendToBack, Undo2, AlignHorizontalJustifyCenter } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { FORMATOS } from "@/lib/carteles";
import { BIBLIOTECA, GRUPOS_SENALES } from "@/lib/senales";

// Editor de carteles personalizados: textos y señales del repositorio sobre una hoja A4/A3.
// Posiciones y anchos en % de la hoja (el mismo diseño sirve en A4 y en A3); letra en puntos sobre A4.
const PX_MM = 96 / 25.4;
const COLORES = ["#000000", "#b00000", "#003399", "#ffffff", "#f5b800"];
const FONDOS = ["", "#cc0000", "#003399", "#f5b800", "#000000", "#ffffff"];
const nid = () => Math.random().toString(36).slice(2, 10);
const lim = (v, a, b) => Math.min(b, Math.max(a, v));

export const medidas = (formato, orientacion) => {
  const f = FORMATOS[formato] || FORMATOS.A4;
  return orientacion === "vertical" ? { w: f.h, h: f.w, k: f.k } : { w: f.w, h: f.h, k: f.k };
};

export const CARTEL_NUEVO = {
  nombre: "Cartel nuevo", formato: "A4", orientacion: "horizontal",
  items: [
    { id: "titulo", tipo: "texto", x: 10, y: 6, w: 80, texto: "La Policía Local INFORMA", tam: 30, negrita: true, color: "#000000", fondo: "", alin: "center" },
    { id: "aviso", tipo: "texto", x: 8, y: 72, w: 84, texto: "Escriba aquí el aviso", tam: 26, negrita: false, color: "#000000", fondo: "", alin: "center" },
  ],
};

// Un elemento del cartel; en la hoja impresa es el mismo DOM sin el recuadro de selección ni el asa
const Elemento = ({ it, k, sel, escala, onDown }) => {
  const S = it.tipo === "senal" ? BIBLIOTECA[it.senal] : null;
  const caja = { position: "absolute", left: `${it.x}%`, top: `${it.y}%`, width: `${it.w}%`, cursor: onDown ? "move" : "default", touchAction: "none", userSelect: "none" };
  const marca = sel ? { outline: `${2 / escala}px dashed #0ea5e9`, outlineOffset: `${3 / escala}px` } : {};
  const asa = sel && onDown && (
    <span className="cartel-asa absolute bg-sky-500 border-white" onPointerDown={(e) => onDown(e, "ancho")}
      style={{ right: -9 / escala, bottom: -9 / escala, width: 18 / escala, height: 18 / escala, borderWidth: 2 / escala, borderRadius: 3 / escala, cursor: "nwse-resize" }} />
  );
  if (S) {
    return (
      <div className="cartel-el" style={{ ...caja, ...marca, aspectRatio: `${1 / (S.ratio || 1)}` }} onPointerDown={onDown && ((e) => onDown(e, "mover"))} data-testid={`cartel-el-${it.id}`}>
        <div style={{ width: "100%", height: "100%", transform: `rotate(${it.rot || 0}deg) scaleX(${it.espejo ? -1 : 1})` }}><S.C valor={it.valor} /></div>
        {asa}
      </div>
    );
  }
  const t = (it.tam || 28) * k;
  return (
    <div className="cartel-el" onPointerDown={onDown && ((e) => onDown(e, "mover"))} data-testid={`cartel-el-${it.id}`}
      style={{ ...caja, ...marca, boxSizing: "border-box", fontFamily: "Arial, Helvetica, sans-serif", fontSize: `${t}pt`, lineHeight: 1.15,
        fontWeight: it.negrita ? 800 : 400, color: it.color || "#000", textAlign: it.alin || "center", background: it.fondo || "transparent",
        padding: it.fondo ? `${t * 0.25}pt ${t * 0.45}pt` : 0, whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
      {it.texto || " "}
      {asa}
    </div>
  );
};

export const HojaPersonalizada = ({ cartel, sel, escala = 1, onDown, onFondo, guia }) => {
  const m = medidas(cartel.formato, cartel.orientacion);
  return (
    <div className="cartel bg-white text-black relative overflow-hidden" style={{ width: `${m.w}mm`, height: `${m.h}mm` }} onPointerDown={onFondo} data-testid="cartel-hoja">
      {cartel.items.map((it) => <Elemento key={it.id} it={it} k={m.k} sel={sel === it.id} escala={escala} onDown={onDown && ((e, modo) => onDown(e, it, modo))} />)}
      {guia && <div className="cartel-asa absolute top-0 bottom-0 left-1/2 border-l border-dashed border-pink-500 pointer-events-none" style={{ borderLeftWidth: 1.5 / escala }} />}
    </div>
  );
};

const Boton = ({ on, children, ...p }) => (
  <button type="button" {...p} className={`h-9 px-2.5 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40 ${on ? "border-sky-500 bg-sky-600 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{children}</button>
);

export default function CartelEditor({ inicial, canEdit, lista, onCambio, onBorrado, setEstado }) {
  const [c, setC] = useState(inicial);
  const [sel, setSel] = useState(null);
  const [guia, setGuia] = useState(false);
  const [ancho, setAncho] = useState(0);
  const marco = useRef(null);
  const arrastre = useRef(null);
  const pasado = useRef([]);
  const ultimo = useRef(0);
  const timer = useRef(null);
  const actual = useRef(c);
  actual.current = c;
  const m = medidas(c.formato, c.orientacion);
  const escala = ancho ? Math.min(1, ancho / (m.w * PX_MM)) : 0.5;
  const escalaRef = useRef(escala);
  escalaRef.current = escala;

  useEffect(() => {
    if (!marco.current) return undefined;
    const ro = new ResizeObserver(([e]) => setAncho(e.contentRect.width));
    ro.observe(marco.current);
    return () => ro.disconnect();
  }, []);

  // guardado automático (y el padre se entera para la lista y el tamaño de papel de la impresión)
  const guardar = (n) => {
    onCambio(n);
    if (!canEdit) return;
    setEstado("guardando");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { id, nombre, formato, orientacion, items } = actual.current;
        await api.put(`/carteles-personalizados/${id}`, { nombre: nombre.trim() || "Cartel sin nombre", formato, orientacion, items });
        setEstado("guardado");
      } catch (e) { setEstado(""); toast.error(errMsg(e)); }
    }, 800);
  };
  // cada cambio guarda; los cambios seguidos (teclear, arrastrar) cuentan como uno para deshacer
  const cambiar = (fn, { historial = true } = {}) => {
    if (!canEdit) return;
    const prev = actual.current;
    const n = fn(prev);
    if (historial && Date.now() - ultimo.current > 800) pasado.current = [...pasado.current.slice(-49), prev];
    ultimo.current = Date.now();
    actual.current = n;
    setC(n);
    guardar(n);
  };
  const deshacer = () => {
    const prev = pasado.current.pop();
    if (!prev) return;
    actual.current = prev;
    setC(prev);
    guardar(prev);
    if (!prev.items.some((i) => i.id === sel)) setSel(null);
  };
  const item = c.items.find((i) => i.id === sel);
  const cambiarItem = (patch) => cambiar((p) => ({ ...p, items: p.items.map((i) => (i.id === sel ? { ...i, ...patch } : i)) }));
  const borrarItem = () => { cambiar((p) => ({ ...p, items: p.items.filter((i) => i.id !== sel) })); setSel(null); };
  const anadir = (it) => {
    if (c.items.length >= 80) return toast.error("Máximo 80 elementos por cartel");
    const n = { id: nid(), ...it };
    pasado.current = [...pasado.current.slice(-49), actual.current];
    ultimo.current = 0;
    const x = { ...actual.current, items: [...actual.current.items, n] };
    actual.current = x;
    setC(x);
    guardar(x);
    setSel(n.id);
  };
  const anadirSenal = (k) => {
    const S = BIBLIOTECA[k];
    const w = S.ratio ? 40 : 26;
    const alto = (w * (S.ratio || 1) * m.w) / m.h;
    const paso = (c.items.length % 5) * 3; // las señales nuevas no caen una encima de otra
    anadir({ tipo: "senal", senal: k, x: 50 - w / 2 + paso, y: lim(50 - alto / 2 + paso, 0, 100), w, valor: S.valor || "", rot: 0, espejo: false });
  };
  const orden = (delante) => cambiar((p) => {
    const it = p.items.find((i) => i.id === sel);
    const resto = p.items.filter((i) => i.id !== sel);
    return { ...p, items: delante ? [...resto, it] : [it, ...resto] };
  });

  // arrastrar para mover; el asa de la esquina cambia el tamaño. Se imanta al centro de la hoja.
  const empezar = (e, it, modo) => {
    e.stopPropagation();
    setSel(it.id);
    if (!canEdit || e.button > 0) return;
    e.preventDefault();
    arrastre.current = { id: it.id, modo, x0: e.clientX, y0: e.clientY, it };
    pasado.current = [...pasado.current.slice(-49), actual.current];
    ultimo.current = Date.now();
  };
  useEffect(() => {
    const mover = (e) => {
      const a = arrastre.current;
      if (!a) return;
      const { w, h } = medidas(actual.current.formato, actual.current.orientacion);
      const dx = ((e.clientX - a.x0) / (escalaRef.current * w * PX_MM)) * 100;
      const dy = ((e.clientY - a.y0) / (escalaRef.current * h * PX_MM)) * 100;
      let patch;
      if (a.modo === "ancho") patch = { w: lim(a.it.w + dx, 3, 100) };
      else {
        let x = lim(a.it.x + dx, -a.it.w + 3, 97);
        const centro = Math.abs(x + a.it.w / 2 - 50) < 1.2;
        if (centro) x = 50 - a.it.w / 2;
        setGuia(centro);
        patch = { x, y: lim(a.it.y + dy, -5, 97) };
      }
      const n = { ...actual.current, items: actual.current.items.map((i) => (i.id === a.id ? { ...i, ...patch } : i)) };
      actual.current = n;
      setC(n);
    };
    const soltar = () => {
      if (!arrastre.current) return;
      arrastre.current = null;
      setGuia(false);
      guardar(actual.current);
    };
    window.addEventListener("pointermove", mover);
    window.addEventListener("pointerup", soltar);
    return () => { window.removeEventListener("pointermove", mover); window.removeEventListener("pointerup", soltar); };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // teclado: Supr borra, flechas mueven, Cmd/Ctrl+Z deshace, Esc deselecciona
  useEffect(() => {
    const tecla = (e) => {
      if (/INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") { e.preventDefault(); deshacer(); return; }
      if (!sel) return;
      if (e.key === "Escape") setSel(null);
      else if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); borrarItem(); }
      else if (e.key.startsWith("Arrow")) {
        e.preventDefault();
        const d = e.shiftKey ? 2 : 0.5;
        const it = actual.current.items.find((i) => i.id === sel);
        if (!it) return;
        cambiarItem(e.key === "ArrowLeft" ? { x: it.x - d } : e.key === "ArrowRight" ? { x: it.x + d } : e.key === "ArrowUp" ? { y: it.y - d } : { y: it.y + d });
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const borrarCartel = async () => {
    if (!window.confirm(`¿Eliminar el cartel «${c.nombre}»? No se puede deshacer.`)) return;
    try { await api.delete(`/carteles-personalizados/${c.id}`); clearTimeout(timer.current); setEstado(""); onBorrado(c.id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const S = item?.tipo === "senal" ? BIBLIOTECA[item.senal] : null;

  return (
    <div className="cartel-rejilla grid lg:grid-cols-[22rem_minmax(0,1fr)] gap-4 p-4 sm:p-6">
      <aside className="no-print bg-white border border-slate-200 rounded-xl p-4 space-y-4 h-fit" data-testid="cartel-editor-panel">
        {lista}
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Nombre del cartel</span>
            <input value={c.nombre} maxLength={80} readOnly={!canEdit} onChange={(e) => cambiar((p) => ({ ...p, nombre: e.target.value }))} data-testid="cartel-nombre"
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:border-sky-500" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(FORMATOS).map((k) => <Boton key={k} on={c.formato === k} disabled={!canEdit} onClick={() => cambiar((p) => ({ ...p, formato: k }))}>{k}</Boton>)}
            {[["horizontal", "Horizontal"], ["vertical", "Vertical"]].map(([v, l]) => (
              <Boton key={v} on={c.orientacion === v} disabled={!canEdit} onClick={() => cambiar((p) => ({ ...p, orientacion: v }))} data-testid={`cartel-orientacion-${v}`}>{l}</Boton>
            ))}
          </div>
          <div className="flex gap-2">
            <Boton disabled={!canEdit} onClick={() => anadir({ tipo: "texto", x: 10, y: 40, w: 80, texto: "Texto", tam: 28, negrita: false, color: "#000000", fondo: "", alin: "center" })} data-testid="cartel-anadir-texto">
              <Type className="w-4 h-4" /> Añadir texto
            </Boton>
            <Boton disabled={!pasado.current.length || !canEdit} onClick={deshacer} title="Deshacer (Cmd/Ctrl+Z)"><Undo2 className="w-4 h-4" /> Deshacer</Boton>
          </div>
        </div>

        {item && (
          <div className="border border-sky-200 bg-sky-50/50 rounded-xl p-3 space-y-3" data-testid="cartel-propiedades">
            <div className="text-xs font-semibold text-slate-600">{S ? `${S.codigo} · ${S.nombre}` : "Texto seleccionado"}</div>
            {item.tipo === "texto" && (
              <>
                <textarea value={item.texto} rows={3} maxLength={1000} onChange={(e) => cambiarItem({ texto: e.target.value })} data-testid="cartel-texto"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-sky-500 bg-white" />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">Tamaño</span>
                  <input type="number" min={4} max={400} value={item.tam} onChange={(e) => cambiarItem({ tam: lim(Number(e.target.value) || 4, 4, 400) })} data-testid="cartel-tam"
                    className="w-20 h-9 rounded-lg border border-slate-300 px-2 text-sm bg-white" />
                  <span className="text-xs text-slate-500">pt</span>
                  <Boton on={item.negrita} onClick={() => cambiarItem({ negrita: !item.negrita })} title="Negrita"><Bold className="w-4 h-4" /></Boton>
                </div>
                <div className="flex gap-1.5">
                  {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([v, I]) => (
                    <Boton key={v} on={item.alin === v} onClick={() => cambiarItem({ alin: v })} title={{ left: "Izquierda", center: "Centro", right: "Derecha" }[v]}><I className="w-4 h-4" /></Boton>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-slate-600 w-12">Letra</span>
                  {COLORES.map((col) => (
                    <button key={col} type="button" onClick={() => cambiarItem({ color: col })} title={col} style={{ background: col }}
                      className={`w-7 h-7 rounded-full border ${item.color === col ? "ring-2 ring-sky-500 ring-offset-1" : "border-slate-300"}`} />
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-slate-600 w-12">Fondo</span>
                  {FONDOS.map((col) => (
                    <button key={col || "no"} type="button" onClick={() => cambiarItem({ fondo: col })} title={col || "Sin fondo"} style={{ background: col || "#fff" }}
                      className={`w-7 h-7 rounded-md border text-[0.6rem] text-slate-400 ${item.fondo === col ? "ring-2 ring-sky-500 ring-offset-1" : "border-slate-300"}`}>{col ? "" : "∅"}</button>
                  ))}
                </div>
              </>
            )}
            {S?.valor !== undefined && (
              <label className="flex items-center gap-2">
                <span className="text-xs text-slate-600">Cifra</span>
                <input value={item.valor} maxLength={3} onChange={(e) => cambiarItem({ valor: e.target.value.replace(/[^0-9]/g, "") })} data-testid="cartel-valor"
                  className="w-20 h-9 rounded-lg border border-slate-300 px-2 text-sm bg-white" />
                <span className="text-xs text-slate-500">{S.etiquetaValor}</span>
              </label>
            )}
            <div className="flex flex-wrap gap-1.5">
              {S && <Boton onClick={() => cambiarItem({ rot: ((item.rot || 0) + 45) % 360 })} title="Girar 45°"><RotateCw className="w-4 h-4" /> Girar</Boton>}
              {S && <Boton onClick={() => cambiarItem({ espejo: !item.espejo })} title="Voltear"><FlipHorizontal className="w-4 h-4" /> Voltear</Boton>}
              <Boton onClick={() => cambiarItem({ x: 50 - item.w / 2 })} title="Centrar en la hoja"><AlignHorizontalJustifyCenter className="w-4 h-4" /> Centrar</Boton>
              <Boton onClick={() => anadir({ ...item, x: item.x + 3, y: item.y + 3 })} title="Duplicar"><Copy className="w-4 h-4" /></Boton>
              <Boton onClick={() => orden(true)} title="Traer al frente"><BringToFront className="w-4 h-4" /></Boton>
              <Boton onClick={() => orden(false)} title="Enviar al fondo"><SendToBack className="w-4 h-4" /></Boton>
              <Boton onClick={borrarItem} title="Eliminar (Supr)" data-testid="cartel-borrar-el"><Trash2 className="w-4 h-4 text-red-600" /></Boton>
            </div>
          </div>
        )}

        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Señales</div>
          <p className="text-[0.6875rem] text-slate-500 mb-2">Pulse una señal para ponerla en el cartel; arrástrela y use la esquina para cambiar el tamaño.</p>
          {Object.entries(GRUPOS_SENALES).map(([g, gl]) => (
            <div key={g} className="mb-2">
              <div className="text-[0.625rem] font-mono tracking-widest uppercase text-slate-500 py-1">{gl}</div>
              <div className="grid grid-cols-5 gap-1.5">
                {Object.entries(BIBLIOTECA).filter(([, s]) => s.grupo === g).map(([k, s]) => (
                  <button key={k} type="button" disabled={!canEdit} onClick={() => anadirSenal(k)} title={`${s.codigo} · ${s.nombre}`} data-testid={`cartel-senal-${k}`}
                    className="aspect-square rounded-lg border border-slate-200 hover:border-sky-400 hover:bg-sky-50 p-1.5 flex items-center justify-center disabled:opacity-40">
                    <span style={{ width: "100%", aspectRatio: `${1 / (s.ratio || 1)}` }}><s.C valor={s.valor} /></span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        {canEdit && (
          <button type="button" onClick={borrarCartel} className="text-xs text-red-600 hover:underline flex items-center gap-1" data-testid="cartel-borrar">
            <Trash2 className="w-3.5 h-3.5" /> Eliminar este cartel
          </button>
        )}
      </aside>

      <section ref={marco} className="cartel-marco flex justify-center items-start overflow-hidden min-w-0" data-testid="cartel-preview">
        <div className="cartel-vista shadow-xl origin-top" style={{ transform: `scale(${escala})`, marginBottom: `${(escala - 1) * m.h * PX_MM}px` }}>
          <HojaPersonalizada cartel={c} sel={sel} escala={escala} guia={guia} onDown={canEdit ? empezar : undefined} onFondo={() => setSel(null)} />
        </div>
      </section>
    </div>
  );
}
